import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Developer } from 'src/app/shared/interfaces/psa-project-register';
import { ProjectTable } from '../project-table/project-table.component';
import { Subscription } from 'rxjs';
import { formatCPFOrCNPJ, formatCpfCnpjInputValue, getDocumentCharacterCount, resolveCpfCnpjInputMask } from 'src/app/helpers/utils/cpf-cnpj.util';
import { CpfCnpjValidator } from 'src/app/helpers/Validators/CpfCnpjValidator/cpf-cnpj.validator';
import { AppToastService } from 'src/app/shared/services/app-toast.service';

@Component({
  standalone: false,
  selector: 'app-psa-add-others-developers',
  templateUrl: './psa-add-others-developers.component.html',
  styleUrls: ['./psa-add-others-developers.component.scss'],
})
export class AddOthersDevelopersComponent extends ProjectTable<Developer> implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE = 5;

  @Input() formRegister!: FormGroup;
  @Output() developersChanged = new EventEmitter<Developer[]>();

  developersAdded: Developer[] = [];
  developersSubscription?: Subscription;

  constructor(private readonly toastService: AppToastService) {
    super();
    this.pagination.page = 1;
    this.pagination.size = AddOthersDevelopersComponent.PAGE_SIZE;
  }



  get developerDocumentMask(): 'text' | 'cnpj' {
    return resolveCpfCnpjInputMask(this.formRegister.get('developerCnpjAdd')?.value);
  }



  get paginatedDevelopers(): Developer[] {

    const page = this.pagination.page ?? 1;

    const size = this.pagination.size ?? AddOthersDevelopersComponent.PAGE_SIZE;

    const start = (page - 1) * size;

    return this.developersAdded.slice(start, start + size);

  }



  get currentPage(): number {

    return this.pagination.page ?? 1;

  }



  ngOnInit(): void {

    const initialDevelopers = this.formRegister.get('developers')?.value;

    if (initialDevelopers && Array.isArray(initialDevelopers)) {

      this.developersAdded = initialDevelopers.filter(d => !d.principal);

      this.updateTableDataOthersDevelopers();

    }



    this.developersSubscription = this.formRegister.get('developers')?.valueChanges.subscribe((value: Developer[]) => {

      if (value && Array.isArray(value)) {

        this.developersAdded = value.filter(d => !d.principal);

        this.updateTableDataOthersDevelopers();

      }

    });

  }



  onChangesInputCpfOrCnpj(): void {
    const control = this.formRegister.get('developerCnpjAdd');
    const next = formatCpfCnpjInputValue(control?.value, true);
    if (control?.value !== next) {
      control?.setValue(next, { emitEvent: false });
    }
  }



  ngOnDestroy(): void {

    this.developersSubscription?.unsubscribe();

  }



  addDevelopers(): void {

    const developerName = this.formRegister.get('developerCompanyNameAdd')?.value?.trim();

    const cpfCnpj = this.formRegister.get('developerCnpjAdd')?.value?.trim();



    if (developerName && cpfCnpj) {

      const developer: Developer = {

        developerName,

        cpfCnpj,

        principal: false,

      };



      const existsDeveloper = this.developersAdded.find(

        d => d.cpfCnpj === developer.cpfCnpj && d.developerName === developer.developerName,

      );



      if (!existsDeveloper) {

        this.developersAdded.push(developer);

        this.updateTableDataOthersDevelopers();

        this.syncDevelopersFormControl();

        this.developersChanged.emit(this.developersAdded);

        this.toastService.show({
          variant: 'success',
          label: 'Desenvolvedor vinculado com sucesso!',
          description: `${developer.developerName} CPF/CNPJ ${this.formatDeveloperDocument(developer.cpfCnpj)}`,
        });

      }



      this.formRegister.get('developerCompanyNameAdd')?.setValue('');

      this.formRegister.get('developerCnpjAdd')?.setValue('');

    }

  }



  deleteDeveloper(developer: Developer): void {

    const index = this.developersAdded.findIndex(

      d => d.cpfCnpj === developer.cpfCnpj && d.developerName === developer.developerName,

    );



    if (index !== -1) {

      const [removed] = this.developersAdded.splice(index, 1);

      this.updateTableDataOthersDevelopers();

      this.syncDevelopersFormControl();

      this.developersChanged.emit(this.developersAdded);

      this.toastService.show({
        variant: 'warning',
        label: 'Desenvolvedor desvinculado!',
        description: `${removed.developerName} CPF/CNPJ ${this.formatDeveloperDocument(removed.cpfCnpj)}`,
      });

    }

  }



  formatDeveloperDocument(value?: string): string {

    return formatCPFOrCNPJ(value ?? '');

  }



  trackByDeveloper(_index: number, developer: Developer): string {

    return `${developer.cpfCnpj ?? ''}-${developer.developerName ?? ''}`;

  }



  onPageChange(currentPage: number): void {

    this.pagination.page = currentPage;

  }



  goToPage(page: number): void {

    if (page < 1 || page > this.totalPages) {

      return;

    }



    this.onPageChange(page);

  }



  checkHasError(fieldName: string, errorCode?: string): boolean {

    const field = this.formRegister.get(fieldName);



    if (field == null || !field.touched) {

      return false;

    }



    if (errorCode == null) {

      return field.invalid;

    }



    return field.hasError(errorCode);

  }



  checkHasErrorDigitCounter(fieldName: string): boolean {
    const field = this.formRegister.get(fieldName);
    const counter = getDocumentCharacterCount(field?.value);

    if (counter === 0 || counter === 11 || counter === 14) {
      return false;
    }

    return true;
  }

  validateDisabledButton(): boolean {
    const len = getDocumentCharacterCount(this.formRegister.get('developerCnpjAdd')?.value);
    return len !== CpfCnpjValidator.cpfLength && len !== CpfCnpjValidator.cnpjLength;
  }



  private syncDevelopersFormControl(): void {

    const principals = ((this.formRegister.get('developers')?.value as Developer[]) || [])

      .filter(d => d.principal);

    this.formRegister.get('developers')?.setValue([...principals, ...this.developersAdded]);

  }



  private updateTableDataOthersDevelopers(): void {

    this.itens = [...this.developersAdded];

    this.totalItens = this.developersAdded.length || 0;

    this.handleTotalPages({ pageSize: this.pagination.size ?? AddOthersDevelopersComponent.PAGE_SIZE });



    if ((this.pagination.page ?? 1) > this.totalPages) {

      this.pagination.page = this.totalPages || 1;

    }

  }

}

